import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Divider,
  Empty,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import serviceInterface from '../../services/service.interface'
import { constants } from '../../constants'
import ExerciseInstructionsModal from '../exercise/exercise.instructions.modal'
import {
  fetchDefaultCarePlan,
  resetClinicCarePlan,
  saveDefaultCarePlan,
} from '../../store/action/defaultcareplan.action'
import {
  FREQUENCY_PRESETS,
  frequencyLabel,
  nextKey,
  rowsFromItems,
  idsOf,
  patchRow,
  removeRow,
  pickValue,
  tagValues,
  applyPickerChange,
  feedbackPayload,
  exercisePayload,
  holdSeconds,
  overlappingWindows,
  overlapKeys,
  describeOverlap,
} from './careplan.rows'

const { Text } = Typography
const DEFAULT_START = -30
const DEFAULT_END = 90
// Questionnaire frequency = days between occurrences within the date range.
// The named presets live in careplan.rows.js; any other positive N shows as
// "Every N days" and is entered via this sentinel option ("Other…").
const DAILY = 1
const BIWEEKLY = 14
const CUSTOM_FREQ = 'custom'
// TracPatch seed fallbacks when the catalog doesn't carry a pinned value.
const TP_DEFAULT_SETS = '3'
const TP_DEFAULT_REPS = '10'

// Friendly dropdown labels for the raw surgery-type names. The stored/queried
// value stays the raw name (knee/hip/pt) — only the display text changes.
const SURGERY_TYPE_LABELS = { knee: 'Knee', hip: 'Hip', pt: 'Physical Therapy' }
const surgeryTypeLabel = (name) =>
  SURGERY_TYPE_LABELS[(name || '').toLowerCase()] || name

const numOr = (v, d) =>
  v === null || v === undefined || isNaN(Number(v)) ? d : Number(v)
const isPainScore = (shortname) =>
  (shortname || '').trim().toLowerCase() === 'pain score'
const isKneeType = (name) => (name || '').toLowerCase().indexOf('knee') !== -1
const isTracPatch = (cat) => !!cat && cat.type === 'TracPatch'
const isTracPatchDevice = (cat) => !!cat && cat.device_type === 'tracpatch'

// Lean clinic-level editor for the DEFAULT care plan self-registered patients
// receive. Pick which devices / questionnaires / exercises are included and a
// simple S±N day range per item; Save persists to the three clinic templates
// (the active careplan, surgery.feedback, the wildcard regimen).
function DefaultCarePlanSettingsForm(props) {
  // Clinic page passes customerid; the company page passes companyid instead
  // (the template a new clinic is created from) — same form, same payload.
  const { customerid, companyid, onSaved, onCancel } = props
  const scopeId = customerid || companyid
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  const [surgeryTypes, setSurgeryTypes] = useState([])
  const [surgeryType, setSurgeryType] = useState(null)
  const [statusMsg, setStatusMsg] = useState(null)

  const [formCatalog, setFormCatalog] = useState([])
  const [exerciseCatalog, setExerciseCatalog] = useState([])
  const [deviceCatalog, setDeviceCatalog] = useState([])

  // Questionnaires and exercises are ordered ROWS with their own keys, so one
  // form or exercise can appear in several date windows (careplan.rows.js).
  // Devices stay a keyed map: { [deviceid]: { startdate, enddate, ... } }.
  const [feedbackRows, setFeedbackRows] = useState([])
  const [exerciseRows, setExerciseRows] = useState([])
  const [deviceMap, setDeviceMap] = useState({})
  // Questionnaire rows whose frequency is being edited as "every N days".
  const [customFreqKeys, setCustomFreqKeys] = useState({})

  // True when the TracPatch entry below was PRE-SELECTED by this form rather
  // than loaded from the database — i.e. it is not saved and patients are not
  // getting it.
  //
  // Without this the pre-selection renders identically to a configured device,
  // and a clinic set up but never saved shows "TracPatch" while self-registering
  // patients silently receive no device at all. That is exactly what happened on
  // prod2 2026-09-03; see docs/BUGS.md.
  const [deviceDefaultUnsaved, setDeviceDefaultUnsaved] = useState(false)

  // Read-only patient-facing instructions preview. The care-plan catalog only
  // carries name/type/sets/reps, so the full exercise (description + media) is
  // lazy-fetched via getAllExercises on first click and cached by _id.
  const [viewExercise, setViewExercise] = useState(null)
  const [fullExerciseMap, setFullExerciseMap] = useState(null)

  const openInstructions = async (id) => {
    let map = fullExerciseMap
    if (!map) {
      try {
        const res = await serviceInterface.ExerciseService().getAllExercises()
        const all = (res && res.exercises) || []
        map = {}
        all.forEach((e) => {
          if (e && e._id) map[e._id] = e
        })
        setFullExerciseMap(map)
      } catch (e) {
        map = {}
      }
    }
    // Fall back to the catalog entry (name only) if the full fetch missed it.
    const cat = exerciseCatalog.find((e) => e._id === id)
    setViewExercise(map[id] || cat || null)
  }

  const isKnee = isKneeType(surgeryType)

  // The Pain Score form id (auto-selected when a device is added).
  const painScoreId = useMemo(() => {
    const f = formCatalog.find((x) => isPainScore(x.shortname))
    return f ? f._id : null
  }, [formCatalog])

  // Questionnaire defaults: Pain Score is daily; everything else is every 2 weeks.
  const feedbackDefaultFor = (id) => ({
    startdate: DEFAULT_START,
    enddate: DEFAULT_END,
    interval: id && id === painScoreId ? DAILY : BIWEEKLY,
    skiponsurgerydate: false,
  })

  // Exercise defaults: TracPatch exercises prefill with their seed-pinned
  // sets/reps (falling back to 3×10); legacy exercises use 3×10.
  const exerciseDefaultFor = (id) => {
    const cat = exerciseCatalog.find((e) => e._id === id)
    const tp = isTracPatch(cat)
    return {
      startdate: DEFAULT_START,
      enddate: DEFAULT_END,
      set:
        tp && cat.requiredSets != null
          ? String(cat.requiredSets)
          : TP_DEFAULT_SETS,
      repeat:
        tp && cat.requiredReps != null
          ? String(cat.requiredReps)
          : TP_DEFAULT_REPS,
      // Whole seconds per repetition; blank = no hold. Not used for
      // TracPatch exercises (their hold comes from the TracPatch panel).
      hold: '',
    }
  }

  // ---- load THIS clinic's surgery types once (not the global list) ----
  useEffect(() => {
    if (!scopeId) return
    let cancelled = false
    serviceInterface
      .DefaultCarePlanService()
      .getSurgeryTypes({ customerid, companyid })
      .then((types) => {
        if (cancelled) return
        const list = types || []
        setSurgeryTypes(list)
        if (list.length) setSurgeryType(list[0].name)
        else setLoading(false)
      })
      .catch(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [scopeId])

  // ---- load the saved plan + catalogs whenever the surgery type changes ----
  useEffect(() => {
    if (!scopeId || !surgeryType) return
    let cancelled = false
    setLoading(true)
    props
      .fetchDefaultCarePlan({ customerid, companyid, surgeryType })
      .then((data) => {
        if (cancelled || !data) return
        const exCatalog = data.exerciseCatalog || []
        const devCatalog = data.deviceCatalog || []
        setFormCatalog(data.formCatalog || [])
        setExerciseCatalog(exCatalog)
        setDeviceCatalog(devCatalog)

        // One row per server item, in server order: a form with a hand-picked
        // day list arrives as several rows, one per uniform run.
        setFeedbackRows(
          rowsFromItems(
            (data.feedback || []).map((f) => ({
              formid: f.formid,
              startdate: f.startdate,
              enddate: f.enddate,
              interval: numOr(f.interval, DAILY),
              skiponsurgerydate: !!f.skiponsurgerydate,
            })),
            'formid'
          )
        )
        setCustomFreqKeys({})

        const eRows = []
        ;(data.exercises || []).forEach((e) => {
          // A SAVED value wins. The seed is a fallback for an entry that has
          // never had one.
          //
          // This reverses an earlier rule -- "TracPatch sets/reps are governed
          // by the seed (and the TracPatch Settings panel), so load the
          // seed-pinned values rather than a possibly-stale saved number".
          // Both halves of that premise are wrong, and together they made the
          // Sets and Reps inputs accept a number, save it, and discard it on
          // reload:
          //
          //   * The care plan's `repeat` IS the patient's prescription.
          //     Verified on prod2 2026-10-15: a patient's configuration read
          //     "REPEAT 10 times - Regimen: knee Default Plan" where the
          //     clinician had set 5, while the SAME save's start date arrived
          //     correctly as S+14. Dates reached the patient; reps did not.
          //   * The TracPatch Settings panel does NOT govern this field. Its
          //     REPS column is `rep_milestone_by_week` (per week); this is the
          //     regimen's flat `repeat`. Same word, different value.
          //
          // The seed's actual purpose is stated where it is produced
          // (physio_server business/defaultcareplan.js): surfaced "so the
          // editor can prefill a TracPatch exercise with its seed defaults ON
          // FIRST ADD". First add is already handled by exerciseDefaultFor();
          // applying it on every load turned a prefill into a permanent
          // override and left the Company AND Clinic care plans -- this same
          // component, scoped 'company' | 'clinic' -- both unable to set reps
          // at all. See docs/BUGS.md.
          const cat = exCatalog.find((c) => c._id === e.exerciseid)
          const tp = isTracPatch(cat)
          const seedSet =
            tp && cat.requiredSets != null ? String(cat.requiredSets) : null
          const seedReps =
            tp && cat.requiredReps != null ? String(cat.requiredReps) : null
          eRows.push({
            exerciseid: e.exerciseid,
            startdate: numOr(e.startdate, DEFAULT_START),
            enddate: numOr(e.enddate, DEFAULT_END),
            set:
              e.set != null && e.set !== ''
                ? String(e.set)
                : seedSet || TP_DEFAULT_SETS,
            repeat:
              e.repeat != null && e.repeat !== ''
                ? String(e.repeat)
                : seedReps || TP_DEFAULT_REPS,
            // Shown as stored (a legacy '1' shows as 1); the clinician clears
            // or corrects it. Blank for TracPatch, whose hold lives elsewhere.
            hold: tp ? '' : holdSeconds(e.hold),
          })
        })
        setExerciseRows(rowsFromItems(eRows, 'exerciseid'))

        const dMap = {}
        ;(data.devices || []).forEach((d) => {
          dMap[d.deviceid] = {
            startdate: numOr(d.startdate, DEFAULT_START),
            enddate: numOr(d.enddate, DEFAULT_END),
            skiponsurgerydate: !!d.skiponsurgerydate,
          }
        })

        const ok = data.status === constants.MESSAGE.SUCCESS

        // Knee default: when the clinic has no devices saved yet, pre-select the
        // TracPatch device as the only device so a Save persists it.
        let preselected = false
        if (ok && isKneeType(surgeryType) && Object.keys(dMap).length === 0) {
          const tpDevice = devCatalog.find((d) => d.device_type === 'tracpatch')
          if (tpDevice) {
            dMap[tpDevice._id] = {
              startdate: DEFAULT_START,
              enddate: DEFAULT_END,
              skiponsurgerydate: false,
            }
            // Flag it as unsaved. The device is in the picker but NOT in the
            // database, so registration will not assign it until Save is clicked.
            preselected = true
          }
        }
        setDeviceDefaultUnsaved(preselected)
        setDeviceMap(dMap)

        setStatusMsg(
          ok
            ? null
            : data.message ||
                (customerid ? 'This clinic' : 'The company') +
                  ' is not linked to this surgery type yet, so its default plan cannot be edited here.'
        )
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeId, surgeryType, reloadKey])

  const patchItem = (map, setMap, id, patch) =>
    setMap({ ...map, [id]: { ...map[id], ...patch } })

  // Pickers (careplan.rows.js): picking an item in the dropdown adds its
  // first row, or another date window when it is already in the plan — the
  // same gesture both times. Removing a tag (×) drops every row of that item.
  // The dropdown entries carry pickValue(id) so they are never "selected"
  // (antd would de-select on the second click); the tags carry the plain id.
  const formLabel = (id) => {
    const f = formCatalog.find((x) => x._id === id)
    return f ? f.shortname : id
  }
  const exerciseLabel = (id) => {
    const e = exerciseCatalog.find((x) => x._id === id)
    return e ? `${e.name}${e.type === 'TracPatch' ? ' (TracPatch)' : ''}` : id
  }
  const handleFeedbackChange = (values) =>
    setFeedbackRows(applyPickerChange(feedbackRows, values, 'formid', feedbackDefaultFor))
  const handleExerciseChange = (values) =>
    setExerciseRows(applyPickerChange(exerciseRows, values, 'exerciseid', exerciseDefaultFor))

  // Frequency control for one questionnaire row — ONE control. The dropdown
  // shows the named presets, the row's own value when it is not a preset
  // ("Every 5 days"), and "Other…". Choosing Other swaps the dropdown for a
  // number box while the clinician types; leaving the box (blur / Enter)
  // brings the dropdown back showing the result. Any positive N is valid; the
  // server stores the expanded days, not a label.
  const frequencyControl = (row) => {
    const value = Number(row.interval) >= 1 ? Math.trunc(Number(row.interval)) : DAILY
    const isPreset = FREQUENCY_PRESETS.some((p) => p.value === value)
    const editing = !!customFreqKeys[row.key]
    const stopEditing = () => {
      const next = { ...customFreqKeys }
      delete next[row.key]
      setCustomFreqKeys(next)
    }
    return (
      <Space size='small'>
        <span style={{ fontSize: 12, color: '#666' }}>Frequency</span>
        {editing ? (
          <Space size={4}>
            <span style={{ fontSize: 12, color: '#666' }}>every</span>
            <InputNumber
              min={1}
              autoFocus
              value={value}
              onChange={(v) =>
                setFeedbackRows(
                  patchRow(feedbackRows, row.key, {
                    interval: Number(v) >= 1 ? Math.trunc(Number(v)) : DAILY,
                  })
                )
              }
              onBlur={stopEditing}
              onPressEnter={stopEditing}
              style={{ width: 64 }}
            />
            <span style={{ fontSize: 12, color: '#666' }}>days</span>
          </Space>
        ) : (
          <Select
            value={value}
            style={{ width: 160 }}
            onChange={(v) => {
              if (v === CUSTOM_FREQ) {
                setCustomFreqKeys({ ...customFreqKeys, [row.key]: true })
                return
              }
              setFeedbackRows(patchRow(feedbackRows, row.key, { interval: v }))
            }}
          >
            {FREQUENCY_PRESETS.map((fr) => (
              <Select.Option key={fr.value} value={fr.value}>
                {fr.label}
              </Select.Option>
            ))}
            {!isPreset && (
              <Select.Option key={value} value={value}>
                {frequencyLabel(value)}
              </Select.Option>
            )}
            <Select.Option key={CUSTOM_FREQ} value={CUSTOM_FREQ}>
              Other (every N days)…
            </Select.Option>
          </Select>
        )}
      </Space>
    )
  }

  // Per-row "Remove" on every row. Removing an item's last row also clears
  // its tag in the picker above (the tags are derived from the rows), and the
  // tag's × still removes every row of the item at once.
  const removeButton = (rows, setRows, row) => (
    <Button
      type='text'
      size='small'
      danger
      icon={<DeleteOutlined />}
      title='Remove this row'
      aria-label='Remove this row'
      onClick={() => setRows(removeRow(rows, row.key))}
    />
  )

  // Devices: reconcile selection, and auto-add Pain Score (daily) only when a
  // TracPatch device is present — Pain Score is TracPatch's companion
  // questionnaire, so other devices (and exercises) don't imply it.
  const handleDeviceChange = (ids) => {
    // Once the user touches the selection it is their choice, not our
    // pre-filled default, so the "pre-selected" wording stops being true. Still
    // unsaved of course — but that is ordinary form state with a Save button
    // beside it, not a value masquerading as configuration.
    setDeviceDefaultUnsaved(false)
    const next = {}
    ids.forEach((id) => {
      next[id] = deviceMap[id] || {
        startdate: DEFAULT_START,
        enddate: DEFAULT_END,
        skiponsurgerydate: false,
      }
    })
    setDeviceMap(next)

    const hasTracPatch = ids.some((id) =>
      isTracPatchDevice(deviceCatalog.find((d) => d._id === id))
    )
    if (hasTracPatch && painScoreId && !feedbackRows.some((r) => r.formid === painScoreId)) {
      setFeedbackRows(
        feedbackRows.concat([
          { key: nextKey(), formid: painScoreId, ...feedbackDefaultFor(painScoreId) },
        ])
      )
    }
  }

  // Two windows of the same exercise or questionnaire must never share a
  // day (careplan.rows.js overlappingWindows). The offending rows are
  // outlined in red with a note under the list, and Save refuses until fixed.
  const exerciseConflicts = useMemo(
    () => overlappingWindows(exerciseRows, 'exerciseid'),
    [exerciseRows]
  )
  const feedbackConflicts = useMemo(
    () => overlappingWindows(feedbackRows, 'formid'),
    [feedbackRows]
  )
  const exerciseConflictKeys = useMemo(() => overlapKeys(exerciseConflicts), [exerciseConflicts])
  const feedbackConflictKeys = useMemo(() => overlapKeys(feedbackConflicts), [feedbackConflicts])

  const handleSave = async () => {
    if (exerciseConflicts.length || feedbackConflicts.length) {
      message.error(
        'Two date windows of the same item overlap. Change the days so each item has at most one window per day, then save.'
      )
      return
    }
    setSaving(true)
    try {
      // One payload entry per row. The server merges questionnaire rows of the
      // same form into one day list; exercise rows stay one regimen row each.
      // TracPatch exercises take their hold from the TracPatch Exercise
      // Settings panel, so the regimen value is sent blank for them and the
      // app never shows a stray one.
      const feedback = feedbackPayload(feedbackRows)
      const exercises = exercisePayload(
        exerciseRows.map((r) =>
          isTracPatch(exerciseCatalog.find((e) => e._id === r.exerciseid)) ? { ...r, hold: '' } : r
        )
      )
      const devices = Object.keys(deviceMap).map((deviceid) => ({
        deviceid,
        startdate: Number(deviceMap[deviceid].startdate),
        enddate: Number(deviceMap[deviceid].enddate),
        interval: 1,
        time: '1',
        skiponsurgerydate: !!deviceMap[deviceid].skiponsurgerydate,
      }))
      const res = await props.saveDefaultCarePlan({
        customerid,
        companyid,
        surgeryType,
        feedback,
        exercises,
        devices,
      })
      if (res && !res.error) {
        setReloadKey((k) => k + 1)
        if (onSaved) onSaved()
      }
    } finally {
      setSaving(false)
    }
  }

  // Clinic scope only: this surgery type's devices, exercises and
  // questionnaires back to the company plan as it is today. Confirmed first;
  // the form then reloads in place, so the company's values are what's shown.
  const companyLabel = (props.company && props.company.name) || 'the company'
  const handleReset = () => {
    Modal.confirm({
      title: `Reset ${surgeryTypeLabel(surgeryType)} to ${companyLabel}'s care plan?`,
      content:
        `This replaces this clinic's devices, exercises and questionnaires for ${surgeryTypeLabel(surgeryType)} ` +
        `with the company care plan as it is today. Unsaved edits are discarded. ` +
        'Patients already enrolled keep their schedules. This cannot be undone.',
      okText: 'Reset',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        setSaving(true)
        try {
          const res = await props.resetClinicCarePlan({ customerid, surgeryType })
          if (res && !res.error) setReloadKey((k) => k + 1)
        } finally {
          setSaving(false)
        }
      },
    })
  }

  // Shared start/end day-offset editor for one row.
  // hasError outlines both boxes in red (an overlapping window).
  const rangeInputs = (item, onPatch, hasError = false) => (
    <Space size='small'>
      <span style={{ fontSize: 12, color: '#666' }}>Start S</span>
      <InputNumber
        value={item.startdate}
        onChange={(v) => onPatch({ startdate: v })}
        style={{ width: 80 }}
        status={hasError ? 'error' : undefined}
      />
      <span style={{ fontSize: 12, color: '#666' }}>End S</span>
      <InputNumber
        value={item.enddate}
        onChange={(v) => onPatch({ enddate: v })}
        style={{ width: 80 }}
        status={hasError ? 'error' : undefined}
      />
    </Space>
  )

  // Red note listing every overlapping pair of one section, or nothing.
  const overlapAlert = (conflicts, labelOf, what) =>
    conflicts.length > 0 && (
      <Alert
        type='error'
        showIcon
        style={{ margin: '8px 0 12px' }}
        message={`Overlapping date windows: ${what} cannot be scheduled twice on the same day`}
        description={
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {conflicts.map((c, i) => (
              <li key={i}>{describeOverlap(c, labelOf)}</li>
            ))}
          </ul>
        }
      />
    )

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin />
      </div>
    )
  }

  if (!surgeryTypes.length) {
    return (
      <Empty
        description='No surgery types configured.'
        style={{ padding: 40 }}
      />
    )
  }

  const notLinked = !!statusMsg

  // For Knee plans, keep TracPatch exercises grouped at the top of both the
  // picker and the selected-rows list. Stable sort preserves existing order
  // within each group.
  const tpRank = (cat) => (isTracPatch(cat) ? 0 : 1)
  const catById = (id) => exerciseCatalog.find((e) => e._id === id)
  const sortedExerciseCatalog = isKnee
    ? [...exerciseCatalog].sort((a, b) => tpRank(a) - tpRank(b))
    : exerciseCatalog
  const exerciseIds = idsOf(exerciseRows, 'exerciseid')
  const sortedExerciseIds = isKnee
    ? exerciseIds.slice().sort((a, b) => tpRank(catById(a)) - tpRank(catById(b)))
    : exerciseIds
  // The rows in the same grouped order, so the table matches the picker.
  const sortedExerciseRows = isKnee
    ? exerciseRows
        .slice()
        .sort((a, b) => tpRank(catById(a.exerciseid)) - tpRank(catById(b.exerciseid)))
    : exerciseRows

  return (
    <div>
      <Row gutter={16} align='middle' style={{ marginBottom: 16 }}>
        <Col flex='auto'>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
            Surgery type
          </div>
          <Select
            value={surgeryType}
            onChange={(v) => setSurgeryType(v)}
            style={{ width: 280 }}
          >
            {surgeryTypes.map((t) => (
              <Select.Option key={t._id} value={t.name}>
                {surgeryTypeLabel(t.name)}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Space>
            {customerid && (
              <Button onClick={handleReset} disabled={saving || notLinked || !surgeryType}>
                Reset to {companyLabel}&apos;s plan
              </Button>
            )}
            {onCancel && (
              <Button onClick={onCancel} disabled={saving}>
                Cancel
              </Button>
            )}
            <Button
              type='primary'
              onClick={handleSave}
              loading={saving}
              disabled={notLinked}
            >
              Save
            </Button>
          </Space>
        </Col>
      </Row>

      <div style={{ color: '#666', marginBottom: 16, fontSize: 13 }}>
        {customerid
          ? 'New patients who self-register on this clinic receive the plan below for the selected surgery type.'
          : 'Every clinic created from now on starts with the plan below as its own, for the selected surgery type.'}{' '}
        Day ranges are relative to the surgery day
        (e.g. Start S -30 = 30 days before surgery, End S +90 = 90 days after).
      </div>

      {notLinked && (
        <Alert
          type='warning'
          showIcon
          style={{ marginBottom: 16 }}
          message='Not editable for this surgery type'
          description={statusMsg}
        />
      )}

      {/* ---------------- DEVICES ---------------- */}
      <Divider orientation='left'>Devices</Divider>
      <Text type='secondary' style={{ fontSize: 12 }}>
        Device selection applies to the selected surgery type only. Selecting a
        TracPatch device automatically adds the Pain Score questionnaire.
      </Text>
      {/* The pre-selected default is NOT saved. Say so, loudly and next to the
          thing it describes — a clinic that looks configured but assigns no
          device to self-registering patients is a silent failure, and the only
          way anyone noticed was a patient reporting a missing Progress Test. */}
      {deviceDefaultUnsaved && (
        <Alert
          type='warning'
          showIcon
          style={{ margin: '12px 0' }}
          message='Not saved yet'
          description={
            (customerid ? 'This clinic has' : 'The company plan has') +
            ' no device saved for this surgery type, so TracPatch has been pre-selected as the default. ' +
            (customerid ? 'Patients who self-register' : 'New clinics') +
            ' will NOT receive it until you click Save below.'
          }
        />
      )}
      <Select
        mode='multiple'
        allowClear
        placeholder='Add devices'
        style={{ width: '100%', margin: '12px 0' }}
        optionFilterProp='children'
        value={Object.keys(deviceMap)}
        onChange={handleDeviceChange}
        disabled={notLinked}
      >
        {deviceCatalog.map((d) => (
          <Select.Option key={d._id} value={d._id}>
            {d.display_name || d.name}
          </Select.Option>
        ))}
      </Select>
      {Object.keys(deviceMap).map((id) => {
        const cat = deviceCatalog.find((d) => d._id === id)
        const item = deviceMap[id]
        return (
          <Row key={id} align='middle' gutter={12} style={{ marginBottom: 8 }}>
            <Col flex='220px'>
              <Text>{cat ? cat.display_name || cat.name : id}</Text>
            </Col>
            <Col>
              {rangeInputs(item, (p) =>
                patchItem(deviceMap, setDeviceMap, id, p)
              )}
            </Col>
            <Col>
              <Checkbox
                checked={item.skiponsurgerydate}
                onChange={(e) =>
                  patchItem(deviceMap, setDeviceMap, id, {
                    skiponsurgerydate: e.target.checked,
                  })
                }
              >
                Skip on surgery date
              </Checkbox>
            </Col>
          </Row>
        )
      })}

      {/* ---------------- QUESTIONNAIRES ---------------- */}
      <Divider orientation='left'>Questionnaires</Divider>
      <Select
        mode='multiple'
        allowClear
        placeholder='Add questionnaires'
        style={{ width: '100%', marginBottom: 12 }}
        optionFilterProp='children'
        labelInValue
        value={tagValues(feedbackRows, 'formid', formLabel)}
        onChange={handleFeedbackChange}
        disabled={notLinked}
      >
        {formCatalog.map((f) => (
          <Select.Option key={f._id} value={pickValue(f._id)}>
            {f.shortname}
          </Select.Option>
        ))}
      </Select>
      {feedbackRows.length > 0 && (
        <Text type='secondary' style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          A questionnaire can have several date windows, each with its own
          frequency — for example daily for the first two weeks, then every 5
          days. To add one, pick the questionnaire again from the list above.
        </Text>
      )}
      {overlapAlert(feedbackConflicts, formLabel, 'a questionnaire')}
      {feedbackRows.map((row) => {
        const cat = formCatalog.find((f) => f._id === row.formid)
        return (
          <Row key={row.key} align='middle' gutter={12} style={{ marginBottom: 8 }}>
            <Col flex='200px'>
              <Text>{cat ? cat.shortname : row.formid}</Text>
            </Col>
            <Col>
              {rangeInputs(
                row,
                (p) => setFeedbackRows(patchRow(feedbackRows, row.key, p)),
                feedbackConflictKeys.has(row.key)
              )}
            </Col>
            <Col>{frequencyControl(row)}</Col>
            <Col>
              <Checkbox
                checked={row.skiponsurgerydate}
                onChange={(e) =>
                  setFeedbackRows(
                    patchRow(feedbackRows, row.key, {
                      skiponsurgerydate: e.target.checked,
                    })
                  )
                }
              >
                Skip on surgery date
              </Checkbox>
            </Col>
            <Col>{removeButton(feedbackRows, setFeedbackRows, row)}</Col>
          </Row>
        )
      })}

      {/* ---------------- EXERCISES ---------------- */}
      <Divider orientation='left'>Exercises</Divider>
      <Select
        mode='multiple'
        allowClear
        placeholder='Add exercises'
        style={{ width: '100%', marginBottom: 12 }}
        optionFilterProp='children'
        labelInValue
        value={sortedExerciseIds.map((id) => ({ value: id, label: exerciseLabel(id) }))}
        onChange={handleExerciseChange}
        disabled={notLinked}
      >
        {sortedExerciseCatalog.map((e) => (
          <Select.Option key={e._id} value={pickValue(e._id)}>
            {exerciseLabel(e._id)}
          </Select.Option>
        ))}
      </Select>
      {overlapAlert(exerciseConflicts, exerciseLabel, 'an exercise')}
      {sortedExerciseRows.length > 0 && (
        <>
          <Text type='secondary' style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
            An exercise can be prescribed in more than one date window, each
            with its own sets and reps — for example pre-op and again from
            week 2. To add one, pick the exercise again from the list above.
          </Text>
          <Table
            size='small'
            pagination={false}
            rowKey='key'
            onRow={(r) => ({
              onClick: () => openInstructions(r.id),
              style: { cursor: 'pointer' },
            })}
            dataSource={sortedExerciseRows.map((row) => {
              const cat = exerciseCatalog.find((e) => e._id === row.exerciseid)
              return { key: row.key, id: row.exerciseid, cat, item: row, isTp: isTracPatch(cat) }
            })}
            columns={[
              {
                title: 'Exercise',
                dataIndex: 'id',
                render: (_, r) => (
                  <span>
                    <Typography.Link
                      onClick={() => openInstructions(r.id)}
                      title='View patient instructions'
                    >
                      {r.cat ? r.cat.name : r.id}
                    </Typography.Link>
                    {r.isTp && (
                      <Tag color='blue' style={{ marginLeft: 6 }}>
                        TracPatch
                      </Tag>
                    )}
                  </span>
                ),
              },
              {
                title: 'Start S',
                width: 110,
                onCell: () => ({ onClick: (e) => e.stopPropagation() }),
                render: (_, r) => (
                  <InputNumber
                    value={r.item.startdate}
                    onChange={(v) =>
                      setExerciseRows(patchRow(exerciseRows, r.key, { startdate: v }))
                    }
                    style={{ width: 80 }}
                    status={exerciseConflictKeys.has(r.key) ? 'error' : undefined}
                  />
                ),
              },
              {
                title: 'End S',
                width: 110,
                onCell: () => ({ onClick: (e) => e.stopPropagation() }),
                render: (_, r) => (
                  <InputNumber
                    value={r.item.enddate}
                    onChange={(v) =>
                      setExerciseRows(patchRow(exerciseRows, r.key, { enddate: v }))
                    }
                    style={{ width: 80 }}
                    status={exerciseConflictKeys.has(r.key) ? 'error' : undefined}
                  />
                ),
              },
              {
                title: 'Hold (s)',
                width: 100,
                onCell: () => ({ onClick: (e) => e.stopPropagation() }),
                render: (_, r) => (
                  <InputNumber
                    min={0}
                    disabled={r.isTp}
                    title={
                      r.isTp
                        ? 'Hold for a TracPatch exercise is fixed per exercise and not set here'
                        : 'Hold each repetition for this many seconds; blank for no hold'
                    }
                    placeholder={r.isTp ? '—' : ''}
                    value={r.isTp || r.item.hold === '' || r.item.hold == null ? null : Number(r.item.hold)}
                    onChange={(v) =>
                      setExerciseRows(
                        patchRow(exerciseRows, r.key, { hold: v == null || Number(v) <= 0 ? '' : String(v) })
                      )
                    }
                    style={{ width: 72 }}
                  />
                ),
              },
              {
                title: 'Sets',
                width: 90,
                onCell: () => ({ onClick: (e) => e.stopPropagation() }),
                render: (_, r) => (
                  <InputNumber
                    min={1}
                    value={Number(r.item.set)}
                    onChange={(v) =>
                      setExerciseRows(patchRow(exerciseRows, r.key, { set: String(v || 1) }))
                    }
                    style={{ width: 64 }}
                  />
                ),
              },
              {
                title: 'Reps',
                width: 90,
                onCell: () => ({ onClick: (e) => e.stopPropagation() }),
                render: (_, r) => (
                  <InputNumber
                    min={1}
                    value={Number(r.item.repeat)}
                    onChange={(v) =>
                      setExerciseRows(patchRow(exerciseRows, r.key, { repeat: String(v || 1) }))
                    }
                    style={{ width: 64 }}
                  />
                ),
              },
              {
                title: '',
                width: 230,
                onCell: () => ({ onClick: (e) => e.stopPropagation() }),
                render: (_, r) => (
                  <Space size={4}>
                    <Button size='small' onClick={() => openInstructions(r.id)}>
                      View instructions
                    </Button>
                    {removeButton(exerciseRows, setExerciseRows, r.item)}
                  </Space>
                ),
              },
            ]}
          />
          {sortedExerciseIds.some((id) =>
            isTracPatch(exerciseCatalog.find((e) => e._id === id))
          ) && (
            <Text type='secondary' style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
              Reps here apply to every exercise, TracPatch included: the patient
              app takes them from this plan. Hold for a TracPatch exercise is fixed
              per exercise and cannot be set here.
            </Text>
          )}
        </>
      )}

      <ExerciseInstructionsModal
        visible={!!viewExercise}
        exercise={viewExercise}
        onClose={() => setViewExercise(null)}
      />
    </div>
  )
}

DefaultCarePlanSettingsForm.propTypes = {
  customerid: PropTypes.string,
  companyid: PropTypes.string,
  onSaved: PropTypes.func,
  onCancel: PropTypes.func,
  company: PropTypes.object,
}

const mapStateToProps = (state) => ({
  company: state.profile.company,
})

const mapDispatchToProps = (dispatch) => ({
  fetchDefaultCarePlan: (params) => dispatch(fetchDefaultCarePlan(params)),
  saveDefaultCarePlan: (params) => dispatch(saveDefaultCarePlan(params)),
  resetClinicCarePlan: (params) => dispatch(resetClinicCarePlan(params)),
})

export default connect(mapStateToProps, mapDispatchToProps)(DefaultCarePlanSettingsForm)
